import { createElement } from '@/_utilities/dom'
import { clamp } from '@/_utilities/math'
import type { CelestialBody } from '@/_types/celestial-body'

/**
 * The holographic info card that appears beside a hovered or selected planet. It
 * shows the service name, domain, and description, and an "Enter Portal" button
 * that activates the body. While a body is active the card tracks its screen
 * position so it stays pinned beside the moving planet.
 */
export class InfoCard {
  public readonly element: HTMLDivElement

  private readonly nameElement: HTMLHeadingElement
  private readonly domainElement: HTMLSpanElement
  private readonly descriptionElement: HTMLParagraphElement
  private readonly enterButton: HTMLButtonElement
  private currentBody: CelestialBody | null

  /**
   * @param {(body: CelestialBody) => void} onEnter - Called when the button is pressed.
   */
  public constructor(onEnter: (body: CelestialBody) => void) {
    this.currentBody = null

    this.element = createElement('div', { className: 'info-card', attributes: { 'aria-hidden': 'true' } })
    this.nameElement = createElement('h2', { className: 'info-card-name' })
    this.domainElement = createElement('span', { className: 'info-card-domain' })
    this.descriptionElement = createElement('p', { className: 'info-card-description' })
    this.enterButton = createElement('button', { className: 'info-card-enter', text: 'Open' })

    const header = createElement('div', { className: 'info-card-header' })
    header.append(this.nameElement, this.domainElement)
    this.element.append(header, this.descriptionElement, this.enterButton)

    this.enterButton.addEventListener('click', () => {
      if (this.currentBody !== null) {
        onEnter(this.currentBody)
      }
    })
  }

  /**
   * Populate and reveal the card for a body.
   *
   * @param {CelestialBody} body - The body to describe.
   * @returns {void}
   */
  public show(body: CelestialBody): void {
    this.currentBody = body
    this.nameElement.textContent = body.name
    this.domainElement.textContent = body.domain
    this.descriptionElement.textContent = body.description
    this.element.style.setProperty('--card-accent', body.accentColour)
    this.element.style.setProperty('--card-glow', body.glowColour)
    this.element.classList.add('is-visible')
    this.element.setAttribute('aria-hidden', 'false')
  }

  /**
   * Hide the card and forget the current body.
   *
   * @returns {void}
   */
  public hide(): void {
    this.currentBody = null
    this.element.classList.remove('is-visible')
    this.element.setAttribute('aria-hidden', 'true')
  }

  /**
   * The body the card is currently describing, if any.
   *
   * @returns {CelestialBody | null} The active body, or null.
   */
  public getCurrentBody(): CelestialBody | null {
    return this.currentBody
  }

  /**
   * Pin the card to a screen position, keeping it within the viewport.
   *
   * @param {number} screenX - The target X in pixels.
   * @param {number} screenY - The target Y in pixels.
   * @returns {void}
   */
  public setScreenPosition(screenX: number, screenY: number): void {
    const margin = 20
    const width = this.element.offsetWidth || 260
    const height = this.element.offsetHeight || 180
    const clampedX = clamp(screenX + 28, margin, window.innerWidth - width - margin)
    const clampedY = clamp(screenY - height / 2, margin, window.innerHeight - height - margin)
    this.element.style.transform = `translate(${clampedX}px, ${clampedY}px)`
  }
}
